"use client";

import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { addCarForCurrentUser, deleteCar, normalizeVehicleApiResponse } from "../lib/vehicleApi";
import {
  DEFAULT_VEHICLE_TYPE,
  VEHICLE_TYPE_OPTIONS,
  deleteVehicle,
  getVehicleTypeLabel,
  normalizeVehicleLicensePlate,
  readStoredVehicles,
  saveVehicle,
  type StoredVehicle,
} from "../lib/vehicles";

const carCatalog: Record<string, string[]> = {
  Toyota: ["Corolla", "Camry", "RAV4"],
  Volkswagen: ["Golf", "Passat", "Tiguan"],
  BMW: ["Seria 3", "Seria 5", "X3"],
  Tesla: ["Model 3", "Model Y", "Model S"],
  Skoda: ["Octavia", "Superb", "Kodiaq"],
};

const DATE_FORMATTER = new Intl.DateTimeFormat("pl-PL", {
  day: "2-digit",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const inputClasses = [
  "w-full rounded-xl border px-4 py-3 text-sm transition",
  "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500",
  "focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-300/60",
  "dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-300/70",
  "dark:focus:border-emerald-400 dark:focus:ring-emerald-500/40",
].join(" ");

const selectClasses = [
  "w-full rounded-xl border px-4 py-3 text-sm transition appearance-none",
  "border-slate-300 bg-white text-slate-900 shadow-[inset_0_1px_0_rgba(148,163,184,0.35)]",
  "focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-300/60 focus:shadow-[0_0_0_4px_rgba(16,185,129,0.15)]",
  "[color-scheme:light] [&>option]:bg-white [&>option]:text-slate-900",
  "dark:border-white/10 dark:bg-neutral-900/80 dark:text-slate-100 dark:shadow-[inset_0_1px_0_rgba(148,163,184,0.12)] dark:[color-scheme:dark] dark:[&>option]:bg-neutral-900 dark:[&>option]:text-slate-100",
  "dark:focus:border-emerald-400 dark:focus:ring-emerald-500/40",
].join(" ");

type Feedback =
  | {
      type: "success" | "error";
      message: string;
    }
  | null;

const brandOptions = Object.keys(carCatalog);

export default function AdminVehicleNewPage() {
  const formSectionRef = useRef<HTMLDivElement | null>(null);
  const [vehicles, setVehicles] = useState<StoredVehicle[]>([]);
  const [licensePlate, setLicensePlate] = useState("");
  const [brand, setBrand] = useState<string>(brandOptions[0] ?? "");
  const [model, setModel] = useState<string>(() => {
    const initialBrand = brandOptions[0];
    return initialBrand ? carCatalog[initialBrand][0] ?? "" : "";
  });
  const [vehicleType, setVehicleType] = useState(DEFAULT_VEHICLE_TYPE);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingPlate, setDeletingPlate] = useState<string | null>(null);

  useEffect(() => {
    const storedVehicles = readStoredVehicles();
    setVehicles(storedVehicles);
    setIsFormOpen(storedVehicles.length === 0);
  }, []);

  useEffect(() => {
    const availableModels = carCatalog[brand] ?? [];
    if (!availableModels.includes(model)) {
      setModel(availableModels[0] ?? "");
    }
  }, [brand, model]);

  const openAddForm = () => {
    setIsFormOpen(true);
    window.setTimeout(() => {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const resetForm = () => {
    setLicensePlate("");
    const resetBrand = brandOptions[0] ?? "";
    setBrand(resetBrand);
    setModel(resetBrand ? carCatalog[resetBrand]?.[0] ?? "" : "");
    setVehicleType(DEFAULT_VEHICLE_TYPE);
  };

  const handlePlateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLicensePlate(normalizeVehicleLicensePlate(event.target.value));
  };

  const handleBrandChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setBrand(event.target.value);
  };

  const handleModelChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setModel(event.target.value);
  };

  const handleVehicleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setVehicleType(event.target.value as typeof DEFAULT_VEHICLE_TYPE);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedPlate = normalizeVehicleLicensePlate(licensePlate);

    if (!normalizedPlate || !/^[A-Z0-9]+$/.test(normalizedPlate)) {
      setFeedback({
        type: "error",
        message: "Numer rejestracyjny może składać się jedynie z liter i cyfr.",
      });
      return;
    }

    if (!brand || !model) {
      setFeedback({
        type: "error",
        message: "Wybierz markę oraz model pojazdu.",
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const response = await addCarForCurrentUser({
        plate: normalizedPlate,
        type: vehicleType,
      });
      const normalizedResponse = normalizeVehicleApiResponse(response.json);

      if (normalizedResponse.status !== null && normalizedResponse.status !== 1) {
        throw new Error(normalizedResponse.message ?? "API nie potwierdziło dodania pojazdu.");
      }

      const savedVehicle = saveVehicle({
        licensePlate: normalizedPlate,
        brand,
        model,
        vehicleType,
      });

      setVehicles(readStoredVehicles());
      setFeedback({
        type: "success",
        message: normalizedResponse.message ?? `Pojazd ${savedVehicle.licensePlate} został zapisany.`,
      });
      resetForm();
      setIsFormOpen(false);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : "Nie udało się dodać pojazdu.";
      setFeedback({
        type: "error",
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVehicle = async (vehicle: StoredVehicle) => {
    const shouldDelete = window.confirm(`Usunąć pojazd ${vehicle.licensePlate}?`);
    if (!shouldDelete) {
      return;
    }

    setDeletingPlate(vehicle.licensePlate);
    setFeedback(null);

    try {
      const response = await deleteCar({
        plate: vehicle.licensePlate,
      });
      const normalizedResponse = normalizeVehicleApiResponse(response.json);

      if (normalizedResponse.status !== null && normalizedResponse.status !== 1) {
        throw new Error(normalizedResponse.message ?? "API nie potwierdziło usunięcia pojazdu.");
      }

      const nextVehicles = deleteVehicle(vehicle.licensePlate);
      setVehicles(nextVehicles);
      setFeedback({
        type: "success",
        message: normalizedResponse.message ?? `Pojazd ${vehicle.licensePlate} został usunięty.`,
      });

      if (nextVehicles.length === 0) {
        setIsFormOpen(true);
      }
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : "Nie udało się usunąć pojazdu.";
      setFeedback({
        type: "error",
        message,
      });
    } finally {
      setDeletingPlate(null);
    }
  };

  const modelOptions = carCatalog[brand] ?? [];

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:text-emerald-600 dark:text-slate-300/80 dark:hover:text-emerald-300"
          >
            {"<-"} Wróć do panelu
          </Link>

          <button
            type="button"
            onClick={openAddForm}
            className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-400/30 transition hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400"
          >
            Dodaj pojazd
          </button>
        </div>

        <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight">Pojazdy</h1>
              <p className="text-sm text-slate-500 dark:text-slate-300/80">
                Zarządzaj zapisanymi pojazdami i przygotuj je do opłacania postoju.
              </p>
            </div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80">
              {vehicles.length} zapisane
            </div>
          </div>

          {feedback ? (
            <div
              className={`mt-6 rounded-xl border px-4 py-3 text-sm ${
                feedback.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
                  : "border-red-200 bg-red-50 text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200"
              }`}
            >
              {feedback.message}
            </div>
          ) : null}

          {vehicles.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50/80 px-6 py-10 text-center dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-lg font-semibold text-slate-900 dark:text-white">Nie masz jeszcze zapisanych pojazdów.</p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-300/75">
                Dodaj pierwszy pojazd, aby od razu móc wybrać go na ekranie płatności.
              </p>
              <button
                type="button"
                onClick={openAddForm}
                className="mt-5 inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-400/30 transition hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400"
              >
                Dodaj pierwszy pojazd
              </button>
            </div>
          ) : (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {vehicles.map((vehicle) => {
                const isDeleting = deletingPlate === vehicle.licensePlate;

                return (
                  <article
                    key={vehicle.id}
                    className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.28)] transition-colors dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">
                          Numer rejestracyjny
                        </p>
                        <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                          {vehicle.licensePlate}
                        </p>
                      </div>
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                        {getVehicleTypeLabel(vehicle.vehicleType)}
                      </span>
                    </div>

                    <p className="mt-5 text-base font-semibold text-slate-900 dark:text-white">
                      {vehicle.brand} {vehicle.model}
                    </p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">
                      Dodano {DATE_FORMATTER.format(new Date(vehicle.createdAt))}
                    </p>

                    <button
                      type="button"
                      disabled={isDeleting || isSubmitting}
                      onClick={() => void handleDeleteVehicle(vehicle)}
                      className="mt-5 inline-flex items-center justify-center rounded-xl border border-rose-300 px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-70 dark:border-rose-400/40 dark:text-rose-200 dark:hover:bg-rose-500/80 dark:hover:text-white"
                    >
                      {isDeleting ? "Usuwanie..." : "Usuń pojazd"}
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section
          ref={formSectionRef}
          className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Dodaj pojazd</h2>
              <p className="text-sm text-slate-500 dark:text-slate-300/80">
                Uzupełnij dane pojazdu, a po zapisaniu pojawi się on od razu na liście.
              </p>
            </div>

            {isFormOpen ? (
              vehicles.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/[0.05]"
                >
                  Schowaj formularz
                </button>
              ) : null
            ) : (
              <button
                type="button"
                onClick={openAddForm}
                className="inline-flex items-center justify-center rounded-xl border border-emerald-300 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-400/40 dark:text-emerald-200 dark:hover:bg-emerald-500/10"
              >
                Otwórz formularz
              </button>
            )}
          </div>

          {isFormOpen ? (
            <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label htmlFor="licensePlate" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Numer rejestracyjny
                </label>
                <input
                  id="licensePlate"
                  name="licensePlate"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  required
                  pattern="[A-Za-z0-9]+"
                  value={licensePlate}
                  onChange={handlePlateChange}
                  placeholder="NP12345"
                  className={inputClasses}
                />
                <p className="text-xs text-slate-500 dark:text-slate-300/70">
                  Dozwolone są jedynie litery oraz cyfry. Znaki specjalne będą odrzucane.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="vehicleType" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Typ pojazdu
                </label>
                <select id="vehicleType" name="vehicleType" value={vehicleType} onChange={handleVehicleTypeChange} className={selectClasses}>
                  {VEHICLE_TYPE_OPTIONS.map((vehicleTypeOption) => (
                    <option key={vehicleTypeOption.value} value={vehicleTypeOption.value}>
                      {vehicleTypeOption.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-300/70">
                  Ten typ będzie używany do wyliczenia ceny postoju według cennika parkingu.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="brand" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Marka
                </label>
                <select id="brand" name="brand" value={brand} onChange={handleBrandChange} className={selectClasses}>
                  {brandOptions.map((brandOption) => (
                    <option key={brandOption} value={brandOption}>
                      {brandOption}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="model" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Model
                </label>
                <select id="model" name="model" value={model} onChange={handleModelChange} className={selectClasses}>
                  {modelOptions.map((modelOption) => (
                    <option key={modelOption} value={modelOption}>
                      {modelOption}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-400/30 transition hover:bg-emerald-500 disabled:opacity-70 dark:bg-emerald-500 dark:hover:bg-emerald-400"
              >
                {isSubmitting ? "Zapisywanie..." : "Zapisz pojazd"}
              </button>
            </form>
          ) : (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50/80 px-6 py-8 text-center dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-base font-semibold text-slate-900 dark:text-white">
                Formularz dodawania jest ukryty.
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-300/75">
                Otwórz go przyciskiem powyżej, jeśli chcesz zapisać kolejny pojazd.
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
